// All 20 dashboard widget components — data comes from a shared prop bag.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ChevronRight, Plus, CalendarPlus, Users, MapPin, Building2,
  ClipboardList, Megaphone, Cake, AlertCircle, Clock, TrendingUp, User as UserIcon,
  Siren, CheckCircle2, TelescopeIcon, PlaneLanding, Send,
  PlaneTakeoff, Wallet, Gauge, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { useDashboardCtx } from "@/components/dashboard/WidgetDetailModal";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import QuickActionModal from "@/components/dashboard/QuickActionModal";

/* --------- Shared UI helpers --------- */
function WidgetShell({ title, hint, icon: Icon, actionTo, actionLabel, testid, children, className = "", widgetId }) {
  const { openDetail } = useDashboardCtx();
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      className={`card overflow-hidden flex flex-col ${className}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b hairline">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && (
            <div className="w-8 h-8 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
              <Icon size={14} strokeWidth={2} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display text-[15px] font-bold leading-tight truncate text-[hsl(var(--text-strong))]">{title}</div>
            {hint && <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 truncate">{hint}</div>}
          </div>
        </div>
        {(widgetId || actionTo) && (
          <button
            type="button"
            onClick={() => widgetId ? openDetail(widgetId) : null}
            className="text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 shrink-0"
            data-testid={`${testid}-open-details`}
          >
            {actionLabel || "Details"} <ArrowUpRight size={11} />
          </button>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </motion.section>
  );
}

function StatCard({ label, value, icon: Icon, accent = "hsl(var(--primary))", widgetId, testid, featured = false }) {
  const { openDetail } = useDashboardCtx();
  if (featured) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <button
          type="button"
          onClick={() => widgetId && openDetail(widgetId)}
          data-testid={testid}
          className="card card-lift relative p-5 h-full w-full text-left flex flex-col justify-between block overflow-hidden"
          style={{
            background: `linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)`,
            border: "1px solid hsl(var(--primary) / 0.2)",
            color: "white",
          }}
        >
          {/* Decorative gradient orb */}
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-25 blur-2xl" style={{ background: "white" }} />
          <div className="relative w-10 h-10 rounded-xl grid place-items-center bg-white/20 backdrop-blur-sm text-white">
            <Icon size={17} strokeWidth={2} />
          </div>
          <div className="relative mt-6">
            <div className="font-display text-[32px] leading-none tracking-tight tabular-nums text-white">
              {value ?? "–"}
            </div>
            <div className="text-[12px] text-white/80 mt-1.5 font-medium">{label}</div>
          </div>
        </button>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        type="button"
        onClick={() => widgetId && openDetail(widgetId)}
        data-testid={testid}
        className="card card-lift p-5 h-full w-full text-left flex flex-col justify-between block"
      >
        <div className="w-10 h-10 rounded-xl grid place-items-center"
             style={{ background: `${accent}18`, color: accent }}>
          <Icon size={17} strokeWidth={1.9} />
        </div>
        <div className="mt-6">
          <div className="font-display text-[32px] leading-none tracking-tight tabular-nums text-[hsl(var(--text-strong))]">
            {value ?? "–"}
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1.5 font-medium">{label}</div>
        </div>
      </button>
    </motion.div>
  );
}

function EmptyRow({ msg }) {
  return (
    <div className="py-4 text-center">
      <PaperPlaneEmpty size={72} />
      <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1">{msg}</div>
    </div>
  );
}

/* --------- Helpers --------- */
function shiftHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return Math.round((mins / 60) * 10) / 10;
}
function fmtHours(h) { const n = Number(h) || 0; return Number.isInteger(n) ? `${n}u` : `${n.toFixed(1)}u`; }
function initials(name) {
  return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}
function Avatar({ name, size = 30 }) {
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] text-white font-semibold grid place-items-center shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials(name)}
    </div>
  );
}

/* ============================================================
 * STAT WIDGETS
 * ============================================================ */
export function StatEmployees({ stats, t }) { return <StatCard testid="w-stat-employees" label={t("total_employees")} value={stats?.employees} icon={Users} widgetId="stat_employees" featured />; }
export function StatShiftsWeek({ stats, t }) { return <StatCard testid="w-stat-shifts-week" label={t("shifts_this_week")} value={stats?.shifts_this_week} icon={ClipboardList} widgetId="stat_shifts_week" />; }
export function StatOpenShifts({ stats, t }) { return <StatCard testid="w-stat-open-shifts" label={t("open_shifts_stat")} value={stats?.open_shifts} icon={AlertCircle} accent="#F59E0B" widgetId="stat_open_shifts" />; }
export function StatPendingLeave({ stats, t }) { return <StatCard testid="w-stat-pending-leave" label={t("pending_leave")} value={stats?.pending_leave} icon={ClipboardList} accent="#8B5CF6" widgetId="stat_pending_leave" />; }
export function StatPendingSwaps({ stats, t }) { return <StatCard testid="w-stat-pending-swaps" label={t("pending_swaps")} value={stats?.pending_swaps} icon={TrendingUp} accent="#10B981" widgetId="stat_pending_swaps" />; }
export function StatPendingTime({ stats, t }) { return <StatCard testid="w-stat-pending-time" label={t("pending_time")} value={stats?.pending_time} icon={Clock} accent="#EF4444" widgetId="stat_pending_time" />; }
export function StatLocations({ stats, t }) { return <StatCard testid="w-stat-locations" label={t("locations")} value={stats?.locations} icon={MapPin} widgetId="stat_locations" />; }
export function StatTeams({ stats, t }) { return <StatCard testid="w-stat-teams" label={t("teams")} value={stats?.teams} icon={Building2} widgetId="stat_teams" />; }

export function StatTotalHours({ weekShifts, t }) {
  const total = (weekShifts || []).reduce((sum, s) => sum + shiftHours(s.start_time, s.end_time), 0);
  return <StatCard testid="w-stat-total-hours" label={t("total_hours")} value={fmtHours(total)} icon={Clock} widgetId="stat_total_hours" />;
}

/* ============================================================
 * LIST WIDGETS
 * ============================================================ */
export function MyShifts({ myShifts, t }) {
  return (
    <WidgetShell widgetId="my_shifts" title={t("w_my_shifts")} hint={t("w_my_shifts_desc")} icon={UserIcon} actionTo="/roster" actionLabel={t("full_roster")} testid="w-my-shifts">
      <div className="divide-y hairline">
        {(!myShifts || myShifts.length === 0) && <EmptyRow msg={t("empty_upcoming_shifts")} />}
        {(myShifts || []).slice(0, 5).map((s, idx) => (
          <motion.div key={s._id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
            className="flex items-center gap-3 px-5 py-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center font-mono text-[12px] font-semibold shrink-0">
              {new Date(s.date).getDate()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">
                {s.role_label || t("shift_default")}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">
                {s.date} · {s.start_time}–{s.end_time} · {fmtHours(shiftHours(s.start_time, s.end_time))}
              </div>
            </div>
            <span className={`chip chip-${s.status === "published" ? "success" : "neutral"} !text-[10px]`}>{t(s.status)}</span>
          </motion.div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function TeamToday({ todayShifts, employees, t }) {
  const byEmployee = {};
  for (const s of todayShifts || []) {
    if (!s.employee_id) continue;
    (byEmployee[s.employee_id] = byEmployee[s.employee_id] || []).push(s);
  }
  const rows = Object.entries(byEmployee).map(([empId, shifts]) => ({
    id: empId,
    name: employees?.find((e) => e._id === empId)?.name || t("employee"),
    shifts: shifts.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")),
  }));
  return (
    <WidgetShell widgetId="team_today" title={t("w_team_today")} hint={t("w_team_today_desc")} icon={Users} actionTo="/roster" actionLabel={t("full_roster")} testid="w-team-today">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {rows.length === 0 && <EmptyRow msg={t("no_data")} />}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-5 py-3">
            <Avatar name={r.name} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{r.name}</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">
                {r.shifts.map((s) => `${s.start_time}–${s.end_time}`).join(" · ")}
              </div>
            </div>
            <span className="text-[11px] font-bold tabular-nums text-[hsl(var(--primary))]">
              {fmtHours(r.shifts.reduce((sum, s) => sum + shiftHours(s.start_time, s.end_time), 0))}
            </span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function RecentActivity({ notifications, t }) {
  const items = (notifications || []).slice(0, 6);
  return (
    <WidgetShell widgetId="recent_activity" title={t("activity")} hint={t("recent")} icon={Megaphone} actionTo="/notifications" actionLabel={t("view_all")} testid="w-recent-activity">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {items.length === 0 && <EmptyRow msg={t("empty_activity")} />}
        {items.map((n) => (
          <div key={n._id} className="px-5 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-[13px] font-semibold truncate flex-1 text-[hsl(var(--text-strong))]">{n.title}</div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))] mt-1.5 shrink-0" />}
            </div>
            {n.body && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 line-clamp-2">{n.body}</div>}
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function RecentNews({ news, t }) {
  const items = (news || []).slice(0, 4);
  return (
    <WidgetShell widgetId="recent_news" title={t("news")} hint={t("w_recent_news_desc")} icon={Megaphone} actionTo="/news" actionLabel={t("view_all")} testid="w-recent-news">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {items.length === 0 && <EmptyRow msg={t("no_data")} />}
        {items.map((n) => (
          <div key={n._id} className="px-5 py-3">
            <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{n.title}</div>
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{n.author_name}</div>
            {n.body && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 line-clamp-2">{n.body}</div>}
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function UpcomingBirthdays({ today, t }) {
  const bd = [...(today?.birthdays_today || []).map((b) => ({ ...b, when: t("today") })), ...(today?.birthdays_upcoming || []).slice(0, 5)];
  return (
    <WidgetShell widgetId="upcoming_birthdays" title={t("birthdays")} hint={t("w_upcoming_birthdays_desc")} icon={Cake} testid="w-upcoming-birthdays">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {bd.length === 0 && <EmptyRow msg={t("no_birthdays_week")} />}
        {bd.map((b) => (
          <div key={b._id} className="flex items-center gap-3 px-5 py-3">
            <Avatar name={b.name} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{b.name}</div>
              {b.job_title && <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{b.job_title}</div>}
            </div>
            <span className="chip chip-warning !text-[10px]">{b.when || b.mmdd}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function PendingLeaveList({ leaves, employees, t }) {
  const items = (leaves || []).filter((l) => l.status === "pending").slice(0, 5);
  return (
    <WidgetShell widgetId="pending_leave_list" title={t("leave_requests")} hint={t("w_pending_leave_list_desc")} icon={ClipboardList} actionTo="/requests" actionLabel={t("view_all")} testid="w-pending-leave">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {items.length === 0 && <EmptyRow msg={t("no_leave_requests")} />}
        {items.map((l) => {
          const emp = employees?.find((e) => e._id === l.employee_id);
          return (
            <div key={l._id} className="flex items-center gap-3 px-5 py-3">
              <Avatar name={emp?.name} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{emp?.name || t("employee")}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">
                  {l.start_date} → {l.end_date}
                </div>
              </div>
              <span className="chip chip-neutral !text-[10px]">{t(l.leave_type || "vacation")}</span>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

export function OpenShiftsList({ weekShifts, t }) {
  const items = (weekShifts || []).filter((s) => !s.employee_id).slice(0, 6);
  return (
    <WidgetShell widgetId="open_shifts_list" title={t("open_shifts")} hint={t("w_open_shifts_list_desc")} icon={AlertCircle} actionTo="/roster" actionLabel={t("full_roster")} testid="w-open-shifts-list">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {items.length === 0 && <EmptyRow msg={t("no_data")} />}
        {items.map((s) => (
          <div key={s._id} className="flex items-center gap-3 px-5 py-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--warning-soft,45_92%_92%))] text-[hsl(45_92%_40%)] grid place-items-center font-mono text-[12px] font-semibold shrink-0">
              {new Date(s.date).getDate()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">
                {s.role_label || t("shift_default")}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">
                {s.date} · {s.start_time}–{s.end_time}
              </div>
            </div>
            <ChevronRight size={13} className="text-[hsl(var(--text-muted))]" />
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function TopHoursEmployees({ weekShifts, employees, t }) {
  const map = {};
  for (const s of weekShifts || []) {
    if (!s.employee_id) continue;
    map[s.employee_id] = (map[s.employee_id] || 0) + shiftHours(s.start_time, s.end_time);
  }
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const max = sorted[0]?.[1] || 1;
  return (
    <WidgetShell widgetId="top_hours_employees" title={t("hours_per_employee")} hint={t("w_top_hours_desc")} icon={UserIcon} testid="w-top-hours">
      <div className="p-5 flex flex-col gap-3">
        {sorted.length === 0 && <EmptyRow msg={t("no_data")} />}
        {sorted.map(([empId, hours], idx) => {
          const emp = employees?.find((e) => e._id === empId);
          const pct = Math.max(4, (hours / max) * 100);
          return (
            <div key={empId} className="flex items-center gap-3">
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))] w-4">{idx + 1}</div>
              <Avatar name={emp?.name} size={26} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] font-semibold text-[hsl(var(--text-strong))] truncate">{emp?.name}</span>
                  <span className="text-[11px] font-bold tabular-nums text-[hsl(var(--primary))]">{fmtHours(hours)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(217_91%_50%)]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

/* ============================================================
 * WIDE WIDGETS
 * ============================================================ */
export function QuickActions({ t }) {
  const [modalAction, setModalAction] = useState(null);
  const actions = [
    { action: "shift", label: t("add_shift"), Icon: CalendarPlus, testid: "w-qa-shift", to: "/roster?new=1" },
    { action: "employee", label: t("add_employee"), Icon: Users, testid: "w-qa-employees" },
    { action: "location", label: t("add_location"), Icon: MapPin, testid: "w-qa-locations" },
    { action: "team", label: t("add_team"), Icon: Building2, testid: "w-qa-teams" },
    { action: "broadcast", label: t("broadcast"), Icon: Megaphone, testid: "w-qa-broadcast" },
    { action: "leave", label: t("leave_requests"), Icon: ClipboardList, testid: "w-qa-leave", to: "/requests" },
  ];
  const navigate = useNavigate();
  return (
    <>
      <WidgetShell widgetId="quick_actions" title={t("quick_actions")} icon={Plus} testid="w-quick-actions">
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {actions.map((a) => (
            <button
              key={a.testid}
              type="button"
              data-testid={a.testid}
              onClick={() => {
                if (a.to) navigate(a.to);
                else setModalAction(a.action);
              }}
              className="rounded-2xl border hairline p-4 flex flex-col items-center gap-2 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))] transition-all group text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] group-hover:bg-[hsl(var(--primary))] group-hover:text-white grid place-items-center transition-colors">
                <a.Icon size={16} strokeWidth={1.9} />
              </div>
              <span className="text-[12px] font-semibold text-[hsl(var(--text-strong))] text-center leading-tight">{a.label}</span>
            </button>
          ))}
        </div>
      </WidgetShell>

      <QuickActionModal
        open={!!modalAction}
        action={modalAction}
        onClose={() => setModalAction(null)}
      />
    </>
  );
}

const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];

export function WeekCalendar({ weekShifts, t }) {
  const today = new Date();
  const day = (today.getDay() + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - day); monday.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    return d;
  });
  const byDate = {};
  for (const s of weekShifts || []) {
    (byDate[s.date] = byDate[s.date] || []).push(s);
  }
  return (
    <WidgetShell widgetId="week_calendar" title={t("this_week")} hint={t("w_week_calendar_desc")} actionTo="/roster" actionLabel={t("full_roster")} testid="w-week-calendar">
      <div className="p-4 grid grid-cols-7 gap-2">
        {days.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const items = byDate[iso] || [];
          const isToday = iso === today.toISOString().slice(0, 10);
          return (
            <div key={iso} className={`rounded-xl border hairline p-2.5 min-h-[110px] ${isToday ? "bg-[hsl(var(--primary-soft))] border-[hsl(var(--primary))]/30" : "bg-[hsl(var(--bg))]"}`}>
              <div className="text-[9px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
              <div className={`font-display text-[16px] tabular-nums font-bold ${isToday ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-strong))]"}`}>{d.getDate()}</div>
              <div className="mt-2 flex flex-col gap-1">
                {items.slice(0, 3).map((s) => (
                  <div key={s._id} className="text-[10px] font-mono tabular-nums bg-[hsl(var(--surface))] rounded-md px-1.5 py-0.5 truncate">
                    {s.start_time}
                  </div>
                ))}
                {items.length > 3 && <div className="text-[10px] text-[hsl(var(--text-muted))] font-semibold">+{items.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

export function HoursBar({ weekShifts, t }) {
  const today = new Date();
  const day = (today.getDay() + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - day); monday.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
  });
  const hoursByDay = {};
  for (const s of weekShifts || []) {
    hoursByDay[s.date] = (hoursByDay[s.date] || 0) + shiftHours(s.start_time, s.end_time);
  }
  const max = Math.max(1, ...Object.values(hoursByDay));
  return (
    <WidgetShell widgetId="hours_bar" title={t("total_hours")} hint={t("w_hours_bar_desc")} testid="w-hours-bar">
      <div className="p-6 grid grid-cols-7 gap-3 items-end" style={{ minHeight: 180 }}>
        {days.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const h = hoursByDay[iso] || 0;
          const pct = Math.max(2, (h / max) * 100);
          const isToday = iso === today.toISOString().slice(0, 10);
          return (
            <div key={iso} className="flex flex-col items-center gap-1.5">
              <div className="text-[11px] font-bold tabular-nums text-[hsl(var(--text-strong))]">{fmtHours(h)}</div>
              <div className="w-full rounded-t-lg" style={{
                height: `${pct}%`, minHeight: 8,
                background: isToday
                  ? "linear-gradient(to top, hsl(var(--primary)), hsl(217 91% 60%))"
                  : "linear-gradient(to top, hsl(var(--primary-soft)), hsl(var(--primary) / 0.4))",
              }} />
              <div className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
              <div className={`text-[10px] tabular-nums ${isToday ? "text-[hsl(var(--primary))] font-bold" : "text-[hsl(var(--text-muted))]"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

/* ============================================================
 * MANAGER-SPECIFIC WIDGETS (new)
 * ============================================================ */
function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export function UrgentOpenShifts({ weekShifts, t }) {
  const now = new Date();
  const in48 = new Date(Date.now() + 48 * 3600 * 1000);
  const items = (weekShifts || [])
    .filter((s) => !s.employee_id)
    .map((s) => ({ ...s, when: new Date(`${s.date}T${s.start_time || "00:00"}:00`) }))
    .filter((s) => s.when >= now && s.when <= in48)
    .sort((a, b) => a.when - b.when);
  return (
    <WidgetShell widgetId="urgent_open_shifts" title={t("w_urgent_open")} hint={t("w_urgent_open_desc")} icon={Siren} actionTo="/roster" actionLabel={t("full_roster")} testid="w-urgent-open">
      <div className="divide-y hairline max-h-[280px] overflow-y-auto">
        {items.length === 0 && <EmptyRow msg={t("no_data")} />}
        {items.map((s) => {
          const hoursLeft = Math.max(0, (s.when - now) / 3600000);
          const urgent = hoursLeft < 24;
          const bg = urgent ? "hsl(var(--danger-soft))" : "hsl(45 92% 92%)";
          const fg = urgent ? "hsl(var(--danger))" : "hsl(45 92% 40%)";
          return (
            <div key={s._id} className="flex items-center gap-3 px-5 py-3">
              <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 font-mono text-[12px] font-bold" style={{ background: bg, color: fg }}>
                {Math.round(hoursLeft)}u
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{s.role_label || t("shift_default")}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">{s.date} · {s.start_time}–{s.end_time}</div>
              </div>
              <span className="chip !text-[10px]" style={{ background: bg, color: fg }}>{urgent ? "<24u" : "<48u"}</span>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

export function PublishStatus({ weekShifts, t }) {
  const today = new Date();
  const day = (today.getDay() + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - day); monday.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
  });
  const rows = days.map((d) => {
    const iso = d.toISOString().slice(0, 10);
    const items = (weekShifts || []).filter((s) => s.date === iso);
    const pub = items.filter((s) => s.status === "published").length;
    const total = items.length;
    const pct = total === 0 ? 100 : Math.round((pub / total) * 100);
    return { iso, weekday: WEEKDAYS[(d.getDay() + 6) % 7], date: d.getDate(), pub, total, pct };
  });
  return (
    <WidgetShell widgetId="publish_status" title={t("w_publish_status")} hint={t("w_publish_status_desc")} icon={CheckCircle2} actionTo="/roster" actionLabel={t("full_roster")} testid="w-publish-status">
      <div className="p-5 flex flex-col gap-2.5">
        {rows.map((r) => {
          const barColor = r.total === 0 ? "hsl(var(--muted))" : r.pct === 100 ? "hsl(var(--success))" : r.pct >= 50 ? "hsl(45 92% 55%)" : "hsl(var(--danger))";
          return (
            <div key={r.iso} className="flex items-center gap-3">
              <div className="w-10 text-[11px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">{r.weekday}</div>
              <div className="flex-1 h-2.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${r.pct}%`, background: barColor }} />
              </div>
              <div className="text-[11px] font-mono tabular-nums text-[hsl(var(--text-muted))] w-14 text-right">
                {r.total === 0 ? "—" : `${r.pub}/${r.total}`}
              </div>
              <div className="text-[11px] font-bold tabular-nums w-10 text-right" style={{ color: r.total === 0 ? "hsl(var(--text-soft))" : barColor }}>
                {r.total === 0 ? "" : `${r.pct}%`}
              </div>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

export function RosterHorizon({ weekShifts, t }) {
  const { openDetail } = useDashboardCtx();
  // We only have weekShifts (next 7d) preloaded. Compute how far ahead is scheduled.
  const dates = (weekShifts || []).map((s) => s.date).filter(Boolean).sort();
  const last = dates[dates.length - 1] ? new Date(dates[dates.length - 1]) : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysAhead = last ? daysBetween(today, last) : 0;
  const accent = daysAhead >= 14 ? "hsl(var(--success))" : daysAhead >= 7 ? "hsl(45 92% 55%)" : "hsl(var(--danger))";
  return (
    <button
      type="button"
      onClick={() => openDetail("roster_horizon")}
      data-testid="w-roster-horizon"
      className="card card-lift p-5 h-full w-full text-left flex flex-col justify-between block"
    >
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${accent}18`, color: accent }}>
        <TelescopeIcon size={17} strokeWidth={1.9} />
      </div>
      <div className="mt-6">
        <div className="font-display text-[32px] leading-none tracking-tight tabular-nums text-[hsl(var(--text-strong))]">
          {daysAhead > 0 ? `+${daysAhead}` : "0"}<span className="text-[16px] text-[hsl(var(--text-muted))] font-medium ml-0.5">{t("days_short")}</span>
        </div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1.5 font-medium">{t("w_roster_horizon")}</div>
        <div className="text-[10px] text-[hsl(var(--text-soft))] mt-1 truncate">
          {last ? `t/m ${last.toLocaleDateString("nl", { day: "numeric", month: "short" })}` : t("no_data")}
        </div>
      </div>
    </button>
  );
}

export function LeaveTakenYTD({ leaves, employees, t }) {
  // Sum approved leave days per employee this year
  const year = new Date().getFullYear();
  const days = {};
  for (const l of leaves || []) {
    if (l.status !== "approved") continue;
    const start = new Date(l.start_date); const end = new Date(l.end_date);
    if (isNaN(start) || isNaN(end)) continue;
    if (start.getFullYear() !== year && end.getFullYear() !== year) continue;
    const d = Math.max(1, Math.round((end - start) / 86400000) + 1);
    days[l.employee_id] = (days[l.employee_id] || 0) + d;
  }
  const sorted = Object.entries(days).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const max = sorted[0]?.[1] || 1;
  return (
    <WidgetShell widgetId="leave_taken_ytd" title={t("w_leave_taken_ytd")} hint={t("w_leave_taken_ytd_desc")} icon={PlaneLanding} actionTo="/requests" actionLabel={t("view_all")} testid="w-leave-taken">
      <div className="p-5 flex flex-col gap-3">
        {sorted.length === 0 && <EmptyRow msg={t("no_data")} />}
        {sorted.map(([empId, count], idx) => {
          const emp = employees?.find((e) => e._id === empId);
          const pct = Math.max(6, (count / max) * 100);
          return (
            <div key={empId} className="flex items-center gap-3">
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))] w-4">{idx + 1}</div>
              <Avatar name={emp?.name} size={26} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] font-semibold text-[hsl(var(--text-strong))] truncate">{emp?.name || t("employee")}</span>
                  <span className="text-[11px] font-bold tabular-nums text-[hsl(var(--primary))]">{count} {t("day_plural")}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[hsl(280_65%_55%)] to-[hsl(217_91%_55%)]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

export function BroadcastComposer({ t }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error(t("error")); return; }
    setBusy(true);
    try {
      await api.post("/notifications/broadcast", { title: title.trim(), body: body.trim(), target });
      toast.success(t("saved"));
      setTitle(""); setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <WidgetShell widgetId="broadcast_composer" title={t("w_broadcast_composer")} hint={t("w_broadcast_composer_desc")} icon={Send} testid="w-broadcast-composer">
      <div className="p-5 flex flex-col gap-2.5">
        <input
          data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={t("title")} className="input-field !h-10 !text-[13px]"
        />
        <textarea
          data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={t("body")} rows={3}
          className="input-field !text-[13px] !py-2 resize-none"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-0.5 text-[11px]">
            {[
              { v: "all", label: t("all") },
              { v: "role", label: t("by_role") },
            ].map((o) => (
              <button
                key={o.v} onClick={() => setTarget(o.v)}
                data-testid={`broadcast-target-${o.v}`}
                className={`h-7 px-3 rounded-full font-semibold transition-all ${target === o.v ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-xs)]" : "text-[hsl(var(--text-muted))]"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <button
            onClick={send} disabled={busy || !title.trim() || !body.trim()}
            data-testid="broadcast-send"
            className="btn btn-primary !h-9 !text-[12px] ml-auto"
          >
            <Send size={12} strokeWidth={2.2} /> {t("send")}
          </button>
        </div>
      </div>
    </WidgetShell>
  );
}

/* ============================================================
 * FASE 1 — NEW WIDGETS (Feb 2026)
 * ============================================================ */

// Employee: annual leave balance — allocated vs approved+pending
export function MyLeaveBalance({ leaveBalance, t }) {
  const { openDetail } = useDashboardCtx();
  const data = leaveBalance || { allocated: 25, approved_days: 0, pending_days: 0, remaining: 25, used_pct: 0 };
  const remaining = data.remaining ?? 0;
  const usedPct = Math.min(100, data.used_pct || 0);
  const accent = remaining <= 3 ? "hsl(0 75% 55%)" : remaining <= 8 ? "hsl(38 92% 50%)" : "hsl(160 65% 42%)";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        type="button"
        onClick={() => openDetail && openDetail("my_leave_balance")}
        data-testid="w-my-leave-balance"
        className="card card-lift p-5 h-full w-full text-left flex flex-col justify-between block"
      >
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${accent}18`, color: accent }}>
            <PlaneTakeoff size={17} strokeWidth={1.9} />
          </div>
          {data.pending_days > 0 && (
            <span className="chip chip-warning !text-[10px]">{data.pending_days} {t("payroll_period_label") ? t("pending") : "pending"}</span>
          )}
        </div>
        <div className="mt-5">
          <div className="font-display text-[32px] leading-none tracking-tight tabular-nums text-[hsl(var(--text-strong))]">
            {remaining}<span className="text-[16px] text-[hsl(var(--text-muted))] font-medium ml-1">/{data.allocated}</span>
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1.5 font-medium">{t("w_my_leave_balance")}</div>
          <div className="mt-3 h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${usedPct}%`, background: accent }} />
          </div>
        </div>
      </button>
    </motion.div>
  );
}

// Employee: expected pay this month
export function MyExpectedPay({ payrollSummary, t, lang }) {
  const { openDetail } = useDashboardCtx();
  const d = payrollSummary || { total_amount: 0, total_hours: 0, currency: "EUR", has_rate: false, shifts_count: 0 };
  const money = new Intl.NumberFormat(lang === "en" ? "en-GB" : "nl-NL", {
    style: "currency", currency: d.currency || "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(d.total_amount || 0);
  const accent = "hsl(217 91% 55%)";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <button
        type="button"
        onClick={() => openDetail && openDetail("my_expected_pay")}
        data-testid="w-my-expected-pay"
        className="card card-lift p-5 h-full w-full text-left flex flex-col justify-between block"
      >
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${accent}18`, color: accent }}>
          <Wallet size={17} strokeWidth={1.9} />
        </div>
        <div className="mt-5">
          {d.has_rate ? (
            <>
              <div className="font-display text-[26px] leading-none tracking-tight tabular-nums text-[hsl(var(--text-strong))]">
                {money}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1.5 font-medium">
                {d.total_hours.toFixed(1)}u · {d.shifts_count} {t("payroll_shifts_count", { n: "" }).trim().split(" ")[0] || "shifts"}
              </div>
            </>
          ) : (
            <>
              <div className="font-display text-[16px] leading-tight tracking-tight text-[hsl(var(--text-muted))] italic">
                {t("payroll_no_rate")}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1.5">
                {d.total_hours?.toFixed(1) || 0}u {t("planned_hours_short") || "gepland"}
              </div>
            </>
          )}
          <div className="text-[12px] text-[hsl(var(--text-strong))] mt-2 font-medium">{t("w_my_expected_pay")}</div>
        </div>
      </button>
    </motion.div>
  );
}

// Employee-friendly: horizontal strip of colleagues with birthdays this week/soon
export function BirthdaysThisWeek({ today, t }) {
  const list = [
    ...(today?.birthdays_today || []).map((b) => ({ ...b, when: t("today"), highlight: true })),
    ...(today?.birthdays_upcoming || []),
  ].slice(0, 8);
  return (
    <WidgetShell widgetId="birthdays_this_week" title={t("w_birthdays_this_week")} hint={t("w_birthdays_this_week_desc")} icon={Cake} testid="w-birthdays-this-week">
      <div className="p-4">
        {list.length === 0 ? (
          <EmptyRow msg={t("no_birthdays_week")} />
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1" data-testid="birthdays-strip">
            {list.map((b) => (
              <div
                key={b._id}
                className="shrink-0 flex flex-col items-center gap-1.5 px-3 py-3 rounded-2xl border hairline bg-[hsl(var(--surface))] min-w-[92px]"
                title={b.name}
              >
                <div className="relative">
                  <Avatar name={b.name} size={40} />
                  {b.highlight && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[hsl(45_95%_60%)] text-white text-[10px] grid place-items-center border-2 border-[hsl(var(--surface))]">
                      🎂
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-semibold text-[hsl(var(--text-strong))] truncate max-w-[80px]">{b.name?.split(" ")[0]}</div>
                <div className={`text-[9px] uppercase font-black tracking-wider ${b.highlight ? "text-[hsl(38_92%_45%)]" : "text-[hsl(var(--text-muted))]"}`}>
                  {b.when || (b.mmdd || "").replace("-", "/")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </WidgetShell>
  );
}

// Manager: contract hours vs planned hours (this week)
export function HoursVsContract({ hoursContract, t }) {
  const rows = (hoursContract?.rows || []).slice(0, 6);
  const flags = { over: 0, under: 0 };
  for (const r of rows) { if (r.status === "over") flags.over++; else if (r.status === "under") flags.under++; }
  return (
    <WidgetShell
      widgetId="hours_vs_contract"
      title={t("w_hours_vs_contract")}
      hint={t("w_hours_vs_contract_desc")}
      icon={Gauge}
      testid="w-hours-vs-contract"
    >
      <div className="p-5 flex flex-col gap-3">
        {rows.length === 0 && <EmptyRow msg={t("no_data")} />}
        {rows.map((r) => {
          const clamped = Math.min(150, Math.max(0, r.pct));
          const barColor = r.status === "over" ? "hsl(0 75% 55%)" : r.status === "under" ? "hsl(38 92% 50%)" : "hsl(160 65% 42%)";
          return (
            <div key={r.user_id} className="flex items-center gap-3" data-testid={`hvc-${r.user_id}`}>
              <Avatar name={r.name} size={28} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12.5px] font-semibold text-[hsl(var(--text-strong))] truncate">{r.name}</span>
                  <span className="text-[11px] font-mono tabular-nums text-[hsl(var(--text-muted))]">
                    <span className="font-bold" style={{ color: barColor }}>{r.planned_hours.toFixed(1)}</span>
                    <span className="opacity-40">/{r.contract_hours.toFixed(0)}u</span>
                  </span>
                </div>
                <div className="relative h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                  {/* 100% mark */}
                  <div className="absolute inset-y-0 w-px bg-[hsl(var(--border))]" style={{ left: `${100 / 1.5}%` }} />
                  <div className="h-full transition-all" style={{ width: `${clamped / 1.5}%`, background: barColor }} />
                </div>
              </div>
              <span className="text-[10px] font-bold tabular-nums w-10 text-right" style={{ color: barColor }}>
                {r.pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
        {(flags.over > 0 || flags.under > 0) && (
          <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1 flex items-center gap-3">
            {flags.over > 0 && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[hsl(0_75%_55%)]" />{flags.over} {t("hvc_over") || "boven contract"}</span>}
            {flags.under > 0 && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[hsl(38_92%_50%)]" />{flags.under} {t("hvc_under") || "onder contract"}</span>}
          </div>
        )}
      </div>
    </WidgetShell>
  );
}

/* ============================================================
 * Renderer — maps widget ID to component
 * ============================================================ */
export const RENDERERS = {
  stat_employees: StatEmployees,
  stat_shifts_week: StatShiftsWeek,
  stat_open_shifts: StatOpenShifts,
  stat_pending_leave: StatPendingLeave,
  stat_pending_swaps: StatPendingSwaps,
  stat_pending_time: StatPendingTime,
  stat_locations: StatLocations,
  stat_teams: StatTeams,
  stat_total_hours: StatTotalHours,
  my_shifts: MyShifts,
  team_today: TeamToday,
  recent_activity: RecentActivity,
  recent_news: RecentNews,
  upcoming_birthdays: UpcomingBirthdays,
  pending_leave_list: PendingLeaveList,
  open_shifts_list: OpenShiftsList,
  top_hours_employees: TopHoursEmployees,
  quick_actions: QuickActions,
  week_calendar: WeekCalendar,
  hours_bar: HoursBar,
  urgent_open_shifts: UrgentOpenShifts,
  publish_status: PublishStatus,
  roster_horizon: RosterHorizon,
  leave_taken_ytd: LeaveTakenYTD,
  broadcast_composer: BroadcastComposer,
  // Fase 1 additions (Feb 2026)
  my_leave_balance: MyLeaveBalance,
  my_expected_pay: MyExpectedPay,
  birthdays_this_week: BirthdaysThisWeek,
  hours_vs_contract: HoursVsContract,
  team_composition: TeamComposition,
};

// ============================================================
// Team composition — donut chart by role (Feb 2026)
// ============================================================
const ROLE_DONUT_COLORS = {
  owner:    "hsl(35 78% 55%)",   // amber
  manager:  "hsl(282 68% 55%)",  // purple
  teamlead: "hsl(152 68% 45%)",  // green
  employee: "hsl(220 68% 55%)",  // blue
};

function TeamComposition({ allUsers = [], t }) {
  // Only count non-deleted, real accounts. Roles outside the 4 canonical ones
  // (e.g. legacy "hr" seeded rows) get bucketed under "employee" to stay honest.
  const counts = { owner: 0, manager: 0, teamlead: 0, employee: 0 };
  for (const u of allUsers) {
    const r = ["owner", "manager", "teamlead", "employee"].includes(u.role) ? u.role : "employee";
    counts[r] += 1;
  }
  const total = counts.owner + counts.manager + counts.teamlead + counts.employee;

  // Build donut segments: array of {role, count, pct, dash, offset}
  const segments = [];
  let cumulative = 0;
  const circumference = 2 * Math.PI * 40; // r=40
  for (const role of ["owner", "manager", "teamlead", "employee"]) {
    const c = counts[role];
    if (c <= 0) continue;
    const pct = c / (total || 1);
    const dash = pct * circumference;
    segments.push({
      role,
      count: c,
      pct,
      dash,
      offset: -cumulative,
    });
    cumulative += dash;
  }

  return (
    <WidgetShell
      widgetId="team_composition"
      title={t("w_team_composition")}
      hint={t("w_team_composition_desc")}
      icon={Users}
      testid="w-team-composition"
    >
      <div className="p-5 flex items-center gap-6" data-testid="team-composition-body">
        {total === 0 ? (
          <div className="w-full text-[12.5px] text-[hsl(var(--text-muted))]">
            {t("no_data")}
          </div>
        ) : (
          <>
            <div className="relative shrink-0" style={{ width: 128, height: 128 }}>
              <svg viewBox="0 0 100 100" width="128" height="128" style={{ transform: "rotate(-90deg)" }} aria-hidden>
                <circle cx="50" cy="50" r="40" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" />
                {segments.map((s) => (
                  <circle
                    key={s.role}
                    cx="50" cy="50" r="40" fill="none"
                    stroke={ROLE_DONUT_COLORS[s.role]}
                    strokeWidth="12"
                    strokeDasharray={`${s.dash} ${circumference}`}
                    strokeDashoffset={s.offset}
                    strokeLinecap="butt"
                  />
                ))}
              </svg>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="font-display text-[26px] leading-none font-bold tabular-nums text-[hsl(var(--text-strong))]">{total}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mt-1">{t("team_composition_total")}</div>
                </div>
              </div>
            </div>
            <ul className="flex-1 grid grid-cols-1 gap-1.5 min-w-0">
              {["owner", "manager", "teamlead", "employee"].map((role) => (
                <li key={role} className="flex items-center gap-2 text-[12.5px]" data-testid={`team-composition-row-${role}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ROLE_DONUT_COLORS[role] }} />
                  <span className="text-[hsl(var(--text))] font-medium truncate flex-1">{t(`role_${role}`)}</span>
                  <span className="tabular-nums font-bold text-[hsl(var(--text-strong))]">{counts[role]}</span>
                  <span className="tabular-nums text-[10.5px] text-[hsl(var(--text-muted))] w-8 text-right">
                    {total > 0 ? `${Math.round((counts[role] / total) * 100)}%` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </WidgetShell>
  );
}
